import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";

const API_URL = process.env.REACT_APP_API_URL || "";

const fmtDate = (d) => {
  if (!d) return "-";
  try {
    return new Date(d).toLocaleDateString("it-IT");
  } catch {
    return d;
  }
};

export default function EventCashEventsPage() {
  const nav = useNavigate();

  const [events, setEvents] = useState([]);
  const [name, setName] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [description, setDescription] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDate, setEditDate] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editActive, setEditActive] = useState(true);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);

  const loadEvents = async () => {
    setErr("");
    try {
      const res = await authFetch(`${API_URL}/api/event-cash/events/`);
      const data = await res.json().catch(() => []);
      if (!res.ok) throw new Error(data?.detail || "Errore caricamento eventi.");
      setEvents(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e.message || "Errore caricamento eventi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const createEvent = async () => {
    const cleanName = name.trim();
    if (!cleanName) {
      setErr("Inserisci il nome evento.");
      return;
    }

    setErr("");
    setMsg("");

    try {
      const res = await authFetch(`${API_URL}/api/event-cash/events/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: cleanName,
          event_date: eventDate || null,
          description,
          active: true,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Errore creazione evento.");

      setName("");
      setEventDate("");
      setDescription("");
      await loadEvents();
      setMsg("Evento creato.");
    } catch (e) {
      setErr(e.message || "Errore creazione evento.");
    }
  };

  const startEdit = (ev) => {
    setEditingId(ev.id);
    setEditName(ev.name || "");
    setEditDate(ev.event_date || "");
    setEditDescription(ev.description || "");
    setEditActive(!!ev.active);
    setErr("");
    setMsg("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditDate("");
    setEditDescription("");
    setEditActive(true);
  };

  const saveEdit = async (ev) => {
    const cleanName = editName.trim();
    if (!cleanName) {
      setErr("Inserisci il nome evento.");
      return;
    }

    setErr("");
    setMsg("");

    try {
      const res = await authFetch(`${API_URL}/api/event-cash/events/${ev.id}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: cleanName,
          event_date: editDate || null,
          description: editDescription,
          active: editActive,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Errore modifica evento.");

      cancelEdit();
      await loadEvents();
      setMsg("Evento modificato.");
    } catch (e) {
      setErr(e.message || "Errore modifica evento.");
    }
  };

  const deleteEvent = async (ev) => {
    const ok = window.confirm(
      `Eliminare definitivamente l'evento "${ev.name}"?\n\nVerranno eliminati anche listino, vendite e statistiche collegate.`
    );
    if (!ok) return;

    const strong = window.prompt(`Per confermare scrivi ELIMINA`);
    if (strong !== "ELIMINA") {
      setErr("Eliminazione annullata: conferma non valida.");
      return;
    }

    setErr("");
    setMsg("");

    try {
      const res = await authFetch(`${API_URL}/api/event-cash/events/${ev.id}/`, {
        method: "DELETE",
      });

      if (!res.ok && res.status !== 204) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || "Errore eliminazione evento.");
      }

      if (editingId === ev.id) cancelEdit();
      await loadEvents();
      setMsg("Evento eliminato.");
    } catch (e) {
      setErr(e.message || "Errore eliminazione evento.");
    }
  };

  return (
    <div className="container py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <div>
          <div className="text-muted small text-uppercase fw-semibold">
            Cassa Eventi
          </div>
          <h1 className="fw-bold mb-1">🎪 I tuoi eventi</h1>
          <div className="text-muted">
            Crea un evento, prepara il listino dedicato e apri la cassa touch.
          </div>
        </div>

        <button
          type="button"
          className="btn btn-outline-secondary rounded-pill px-4"
          onClick={() => nav("/gestione-app-clienti")}
        >
          ← Gestione App Clienti
        </button>
      </div>

      {err && <div className="alert alert-danger rounded-4">{err}</div>}
      {msg && <div className="alert alert-success rounded-4">{msg}</div>}

      <div className="row g-3">
        <div className="col-12 col-lg-4">
          <div className="card border-0 shadow rounded-4 h-100">
            <div className="card-body p-4">
              <h4 className="fw-bold mb-3">➕ Crea evento</h4>

              <label className="form-label">Nome evento</label>
              <input
                className="form-control mb-3"
                placeholder="Es. Festa della Birra 2026"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />

              <label className="form-label">Data evento</label>
              <input
                type="date"
                className="form-control mb-3"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
              />

              <label className="form-label">Note / descrizione</label>
              <textarea
                className="form-control mb-3"
                rows={4}
                placeholder="Note interne, luogo, organizzazione..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />

              <button className="btn btn-success w-100" onClick={createEvent}>
                Crea evento
              </button>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-8">
          <div className="card border-0 shadow rounded-4">
            <div className="card-body p-4">
              <h4 className="fw-bold mb-3">📋 Eventi creati</h4>

              {loading ? (
                <div>Caricamento...</div>
              ) : events.length === 0 ? (
                <div className="alert alert-info rounded-4">
                  Nessun evento creato.
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {events.map((ev) => {
                    const isEditing = editingId === ev.id;

                    return (
                      <div
                        key={ev.id}
                        className="border rounded-4 p-3 d-flex flex-wrap justify-content-between align-items-start gap-3"
                      >
                        <div className="flex-grow-1">
                          {isEditing ? (
                            <div className="row g-2">
                              <div className="col-12">
                                <label className="form-label small mb-1">Nome evento</label>
                                <input
                                  className="form-control"
                                  value={editName}
                                  onChange={(e) => setEditName(e.target.value)}
                                />
                              </div>

                              <div className="col-12 col-md-5">
                                <label className="form-label small mb-1">Data</label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={editDate || ""}
                                  onChange={(e) => setEditDate(e.target.value)}
                                />
                              </div>

                              <div className="col-12 col-md-7 d-flex align-items-end">
                                <label className="form-check mb-2">
                                  <input
                                    type="checkbox"
                                    className="form-check-input me-2"
                                    checked={editActive}
                                    onChange={(e) => setEditActive(e.target.checked)}
                                  />
                                  Evento attivo
                                </label>
                              </div>

                              <div className="col-12">
                                <label className="form-label small mb-1">Note</label>
                                <textarea
                                  className="form-control"
                                  rows={3}
                                  value={editDescription}
                                  onChange={(e) => setEditDescription(e.target.value)}
                                />
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="fw-bold fs-5">{ev.name}</div>
                              <div className="text-muted small">
                                Data: {fmtDate(ev.event_date)} · Stato:{" "}
                                {ev.active ? "Attivo" : "Chiuso"}
                              </div>
                              {ev.description && (
                                <div className="text-muted mt-1">{ev.description}</div>
                              )}
                              <div className="small mt-2">
                                Articoli listino: {(ev.items || []).length}
                              </div>
                            </>
                          )}
                        </div>

                        <div className="d-flex flex-column flex-sm-row gap-2">
                          {isEditing ? (
                            <>
                              <button
                                className="btn btn-success"
                                onClick={() => saveEdit(ev)}
                              >
                                Salva
                              </button>
                              <button
                                className="btn btn-outline-secondary"
                                onClick={cancelEdit}
                              >
                                Annulla
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="btn btn-primary"
                                onClick={() => nav(`/gestione-app-clienti/cassa-eventi/${ev.id}`)}
                              >
                                Apri cassa
                              </button>
                              <button
                                className="btn btn-outline-secondary"
                                onClick={() => startEdit(ev)}
                              >
                                Modifica
                              </button>
                              <button
                                className="btn btn-outline-danger"
                                onClick={() => deleteEvent(ev)}
                              >
                                Elimina
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
